<template>
  <div class="tinymce-component">
    <Editor v-model="content" :init="initOptions" outputFormat="html" @focus="onFocus" @blur="onBlur" />
    <span v-if="placeholder && visiblePlaceholder && !content" class="tinymce-component__label">{{ placeholder }}</span>
  </div>
</template>

<script>
  // Missing plugin
  import Editor from '@tinymce/tinymce-vue';
  import contentCss from 'tinymce/skins/content/default/content.css';
  import contentUiSkinCss from 'tinymce/skins/ui/oxide/content.css';
  import 'tinymce/tinymce';
  import 'tinymce/models/dom';
  import 'tinymce/themes/silver';
  import 'tinymce/icons/default';
  import 'tinymce/skins/ui/oxide/skin.css';
  /* Import plugins */
  import 'tinymce/plugins/advlist';
  import 'tinymce/plugins/autolink';
  import 'tinymce/plugins/lists';
  import 'tinymce/plugins/image';
  import 'tinymce/plugins/charmap';
  import 'tinymce/plugins/preview';
  import 'tinymce/plugins/anchor';
  import 'tinymce/plugins/searchreplace';
  import 'tinymce/plugins/visualblocks';
  import 'tinymce/plugins/code';
  import 'tinymce/plugins/fullscreen';
  import 'tinymce/plugins/insertdatetime';
  import 'tinymce/plugins/media';
  import 'tinymce/plugins/table';
  import 'tinymce/plugins/help';
  import 'tinymce/plugins/wordcount';
  import 'tinymce/plugins/link';
  import 'tinymce/plugins/codesample';
  import '@/components/tinymce/langs/ru';

  export default {
    name: 'TinyMceEditor',
    components: {
      Editor,
    },
    props: {
      modelValue: String,
      placeholder: {
        type: String,
        default: '',
      },
      init: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        content: this.modelValue,
        visiblePlaceholder: true,
      };
    },
    computed: {
      initOptions() {
        const initFromProps = this.init && Object.keys(this.init).length > 0 ? this.init : {};
        const externalSetup = typeof initFromProps.setup === 'function' ? initFromProps.setup : null;
        const defaultContentStyle = `${contentUiSkinCss.toString()}\n${contentCss.toString()}`;
        const customContentStyle = initFromProps.content_style;
        const mergedContentStyle = [defaultContentStyle, customContentStyle].filter(Boolean).join('\n');
        const defaultPlugins = [
          'advlist',
          'autolink',
          'lists',
          'link',
          'charmap',
          'preview',
          'anchor',
          'searchreplace',
          'visualblocks',
          'code',
          'media',
          'table',
          'wordcount',
        ];
        const resolvedPlugins =
          Array.isArray(initFromProps.plugins) && initFromProps.plugins.length ? initFromProps.plugins : defaultPlugins;

        const commonInit = {
          contextmenu: 'customPaste image table',
          setup: (editor) => {
            const self = this;

            editor.ui.registry.addContextMenu('customPaste', {
              update: () => {
                return [
                  'cut',
                  'copy',
                  {
                    icon: 'paste',
                    text: 'Paste',
                    shortcut: 'Ctrl+V',
                    onAction: () => {
                      self.modernPaste(editor, true);
                    },
                  },
                  {
                    icon: 'paste',
                    text: 'Вставить без форматирования',
                    shortcut: 'Ctrl+Shift+V',
                    onAction: () => {
                      self.modernPaste(editor, false);
                    },
                  },
                  { type: 'separator' },
                  {
                    icon: 'link',
                    text: 'Ссылка...',
                    onAction: () => {
                      editor.execCommand('mceLink', false, { dialog: true });
                    },
                  },
                ];
              },
            });

            editor.on('paste', (event) => {
              const clipboardData = event.clipboardData;
              if (!clipboardData) return;

              event.preventDefault();
              const isPlainPaste = event.shiftKey;
              const htmlData = clipboardData.getData('text/html');
              const plainText = clipboardData.getData('text/plain');

              if (!isPlainPaste && htmlData) {
                editor.insertContent(htmlData);
                return;
              }

              if (!plainText) return;
              const encodedText = editor.dom.encode(plainText).replace(/\r?\n/g, '<br />');
              editor.insertContent(encodedText);
            });

            if (externalSetup) externalSetup(editor);
          },
        };
        const defaultInit = {
          height: 500,
          menubar: false,
          plugins: defaultPlugins,
          toolbar: [
            'undo redo',
            'blocks',
            'bold italic backcolor forecolor',
            'alignleft aligncenter alignright alignjustify',
            'bullist numlist outdent indent',
            'preview removeformat',
            'help',
          ].join(' | '),
          block_formats:
            'Абзац=p;Заголовок 1=h1;Заголовок 2=h2;Заголовок 3=h3;Заголовок 4=h4;Заголовок 5=h5;Заголовок 6=h6',
          language: 'ru',
        };

        return {
          ...defaultInit,
          ...initFromProps,
          skin: false,
          content_css: false,
          content_style: mergedContentStyle,
          plugins: resolvedPlugins,
          ...commonInit,
        };
      },
    },
    created() {
      if (this.modelValue) this.content = this.modelValue;
      else this.content = '';
    },
    watch: {
      modelValue: function (newValue) {
        this.content = newValue ?? '';
      },
      content: function (newValue) {
        this.$emit('update:modelValue', newValue ?? '');
      },
    },
    methods: {
      onFocus() {
        this.visiblePlaceholder = false;
      },
      onBlur() {
        this.visiblePlaceholder = true;
      },
      async modernPaste(editor, checkFormat) {
        try {
          if (navigator.clipboard && navigator.clipboard.readText) {
            this.pasteWithClipboardApi(editor, checkFormat);
          } else {
            editor.notificationManager.open({
              type: 'error',
              text: 'Ваш браузер устарел и не поддерживается.',
            });
          }
        } catch (error) {
          editor.notificationManager.open({
            type: 'error',
            text: 'Ваш браузер не поддерживает доступ к буферу обмена. Используйте следующие сочетания клавиш: Ctrl+X/C/V.',
          });
        }
      },
      async pasteWithClipboardApi(editor, checkFormat) {
        const permission = await navigator.permissions.query({
          name: 'clipboard-read',
        });

        if (permission.state === 'granted' || permission.state === 'prompt') {
          let result;
          const clipboardItems = await navigator.clipboard.read();
          for (const item of clipboardItems) {
            if (checkFormat && item.types.includes('text/html')) {
              const htmlBlob = await item.getType('text/html');
              result = await htmlBlob.text();
            } else if (item.types.includes('text/plain')) {
              const textBlob = await item.getType('text/plain');
              result = await textBlob.text();
            }
          }
          editor.insertContent(result);
        } else if (permission.state === 'denied') {
          editor.notificationManager.open({
            type: 'error',
            text: 'Ваш браузер блокирует доступ к буферу обмена. Используйте следующие сочетания клавиш: Ctrl+X/C/V.',
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  .tinymce-component {
    position: relative;

    &__label {
      position: absolute;
      top: 55px;
      left: 14px;
      font: $fira-14-20;
      color: $dark-grey-1;
      z-index: 1;
      pointer-events: none;
    }

    .tox-edit-area {
      padding: 0 6px;
    }
  }
</style>
